import { type ReactNode, useEffect, useRef, useState } from "react";
import type { BillingSummary, Me } from "./api.ts";
import { PIN } from "./HowItWorks.tsx";
import { buyFrequentFlyer, renewal } from "./paying.ts";
import { HaveACode } from "./Redeem.tsx";
import { Button } from "./souvenir.ts";
import { ButtonLink, CopyButton, Prompt } from "./ui.tsx";

// Your first trip (features/getting-started.md): Your trips, while someone has no trip at all. One prompt
// that needs no editing, the AI asks the rest; copying it turns the screen into the wait, which is never
// an empty page: it says what to do, then, the moment their AI calls Tripmo, that it's working on it
// (each call keeps that fresh), then the trip itself (TripIsHere).

export const FIRST_TRIP =
  "Let's plan a trip in Tripmo. Ask me where I'm going, roughly when and who's coming, then start the trip. If I already have notes or a spreadsheet, I'll paste them in for you to bring everything in.";

// After this long without a call, their AI is likely waiting on them: say to answer it.
const QUIET_MS = 30_000;

// Copied on this device (for this person): the screen is the wait, reloaded too.
const COPIED = "tripmo:first-trip-copied";
function savedCopied(person: string): boolean {
  try {
    return localStorage.getItem(COPIED) === person;
  } catch {
    return false;
  }
}
function keepCopied(person: string | null) {
  try {
    if (person) localStorage.setItem(COPIED, person);
    else localStorage.removeItem(COPIED);
  } catch {}
}
// The trip came: the next time there's no trip, the screen starts from the prompt again.
export const forgetFirstTripCopied = () => keepCopied(null);

// `activeAt`: when this page last heard their AI calling Tripmo (null: not since it opened). Paying
// (features/paying.md): on a plan, New trip (`onNewTrip`) is the main action, above the prompt; with
// nothing that pays for a trip yet, the prompt still works (the AI hands back a pay link) and the screen
// says a pass is needed, with Get a Trip Pass (`buyHref`).
export function FirstTrip({
  me,
  activeAt,
  billing,
  onNewTrip,
  buyHref,
}: {
  me: Me;
  activeAt: number | null;
  billing: BillingSummary | null;
  onNewTrip: (() => void) | null;
  buyHref: string;
}) {
  const [copied, setCopied] = useState(() => savedCopied(me.user.id));
  const waiting = copied || activeAt !== null;
  // Ticks while their AI is working, so a long quiet turns into "answer it there".
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (activeAt === null) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(timer);
  }, [activeAt]);
  useEffect(() => {
    const before = document.title;
    document.title = "Start your first trip · Tripmo";
    return () => {
      document.title = before;
    };
  }, []);

  let wait: ReactNode = null;
  if (waiting) {
    const working = activeAt !== null;
    const quiet = working && now - activeAt >= QUIET_MS;
    wait = (
      <section className={`tm-first-wait${working ? " is-working" : ""}`} aria-live="polite" aria-label="Your trip">
        <WorkingArt working={working} beat={activeAt} />
        <div className="tm-first-wait-text">
          <p className="title">
            {working ? "Your AI is working on it…" : "Paste it into your AI and answer its questions."}
          </p>
          <p className="callout tm-muted">
            {quiet ? "If it asked you something, answer it there. " : ""}Your trip appears here as soon as it's saved.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="tm-first" aria-labelledby="first-title">
      <h1 id="first-title" className="display-l">
        Start your first trip
      </h1>
      {waiting ? (
        <>
          {wait}
          <CopyButton text={FIRST_TRIP} variant="quiet" size="sm" label="Copy the message again" className="tm-start" />
        </>
      ) : (
        <>
          {onNewTrip && (
            <>
              <p className="body tm-muted">Name it here, and your AI plans it with you.</p>
              <Button variant="primary" icon="plus" className="tm-first-new" onClick={onNewTrip}>
                New trip
              </Button>
            </>
          )}
          <p className="body tm-muted">
            {onNewTrip
              ? "Or send this to your AI. It asks you a few questions, then starts the trip here."
              : "Send this to your AI. It asks you a few questions, then starts the trip here."}
          </p>
          <Prompt
            text={FIRST_TRIP}
            primary={!onNewTrip}
            label="What to send your AI"
            onCopied={() => {
              setCopied(true);
              keepCopied(me.user.id);
            }}
          />
        </>
      )}
      {billing && billing.plan === "none" && !billing.can_create_trip && (
        <div className="tm-first-pass">
          <p className="callout tm-muted">
            You'll need a Trip Pass ({billing.prices.trip_pass}, up to 5 people) or{" "}
            {billing.buying ? (
              <a className="tm-first-plan" href={buyFrequentFlyer("/")}>
                Frequent Flyer
              </a>
            ) : (
              "Frequent Flyer"
            )}{" "}
            ({billing.prices.frequent_flyer} a year, every trip included).
          </p>
          {billing.buying && (
            <p className="callout tm-muted">{renewal(billing.prices.frequent_flyer, { named: true })}</p>
          )}
          {billing.buying && (
            <div className="tm-buy-pass-row">
              <ButtonLink href={buyHref} variant="secondary" size="sm" className="tm-buy-pass">
                Get a Trip Pass · {billing.prices.trip_pass}
              </ButtonLink>
              <HaveACode />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// The strip of map the wait shows: a quiet dot while it waits for the AI; once it's working, a pin drops
// on it with each call heard (`beat` changes), up to a handful.
function WorkingArt({ working, beat }: { working: boolean; beat: number | null }) {
  const [pins, setPins] = useState(0);
  useEffect(() => {
    if (beat !== null) setPins((n) => Math.min(n + 1, SPOTS.length));
  }, [beat]);
  return (
    <svg className="tm-art tm-first-art" viewBox="0 0 320 96" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect className="a-sea" width="320" height="96" />
      <path className="a-land" d="M-10 24C40 10 92 32 142 22S236 6 330 26V76C282 90 232 70 180 80S66 94-10 78Z" />
      {working ? (
        SPOTS.slice(0, Math.max(pins, 1)).map(([x, y], i) => (
          <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
            <g className="a-pin a-pin-drop">
              <path className={`a-pin-head a-fill-${COLORS[i % COLORS.length]}`} d={PIN} />
              <circle className="a-pin-eye" cx="0" cy="-14.6" r="3.2" />
            </g>
          </g>
        ))
      ) : (
        <circle className="a-waiting" cx="160" cy="54" r="7" />
      )}
    </svg>
  );
}

const SPOTS: [number, number][] = [
  [120, 60],
  [178, 46],
  [214, 70],
  [84, 74],
  [252, 52],
];
const COLORS = ["sun", "coral", "sea", "moss"];

// The first trip, the moment it's here: its card (standing out) and Open your trip.
export function TripIsHere({ card, onOpen }: { card: ReactNode; onOpen: () => void }) {
  // Read out where the person is looking: the screen changed under them, by itself.
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  useEffect(() => {
    const before = document.title;
    document.title = "Your trip is here · Tripmo";
    return () => {
      document.title = before;
    };
  }, []);
  return (
    <section className="tm-first" aria-labelledby="here-title">
      <h1 id="here-title" className="display-l" ref={heading} tabIndex={-1}>
        Your trip is here
      </h1>
      <div className="tm-first-card">{card}</div>
      <Button variant="primary" block className="tm-first-open" onClick={onOpen}>
        Open your trip
      </Button>
    </section>
  );
}
